import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Loader2, MapPin, Phone, Check, X, Play, CheckCircle2, Lock,
} from 'lucide-react';
import StatusBadge from '@/components/StatusBadge';
import { formatGBP, formatDate, shortId } from '@/lib/utils';

const ACCEPTED_STATUSES = new Set(['accepted', 'confirmed', 'in_progress', 'completed']);

function BookingCard({ booking, onAction, busy }) {
  const accepted = ACCEPTED_STATUSES.has(booking.status);
  const showAccept = booking.status === 'requested';
  const showInProgress = booking.status === 'confirmed' || booking.status === 'accepted';
  const showComplete = booking.status === 'in_progress';

  return (
    <Card className="p-4 border-slate-200 space-y-3" data-testid={`booking-card-${booking.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{booking.service_name || 'Service'}</p>
          <p className="text-[11px] text-slate-500 font-mono">#{shortId(booking.id)}</p>
        </div>
        <StatusBadge value={booking.status} />
      </div>

      <div className="text-xs text-slate-600 space-y-1">
        <p className="flex items-center gap-1">
          <span className="font-medium">{booking.customer_first_name || 'Customer'}</span>
        </p>
        {accepted && booking.customer_address ? (
          <>
            <p className="flex items-start gap-1">
              <MapPin className="h-3 w-3 mt-0.5 shrink-0 text-emerald-600" />
              <span data-testid={`booking-address-${booking.id}`}>{booking.customer_address}</span>
            </p>
            {booking.customer_phone && (
              <p className="flex items-center gap-1">
                <Phone className="h-3 w-3 text-emerald-600" />
                <span data-testid={`booking-phone-${booking.id}`}>{booking.customer_phone}</span>
              </p>
            )}
          </>
        ) : (
          <p className="flex items-center gap-1 text-slate-500">
            <Lock className="h-3 w-3" data-testid={`booking-address-locked-${booking.id}`} />
            <span>{booking.customer_postcode_area || 'Postcode area hidden'} · Full address revealed after you accept</span>
          </p>
        )}
        <p className="text-[11px] text-slate-400">{formatDate(booking.created_at)}</p>
      </div>

      <div className="grid grid-cols-3 gap-2 text-[11px] bg-slate-50 rounded-lg p-2">
        <div>
          <p className="text-slate-400">Service</p>
          <p className="font-semibold text-slate-900">{formatGBP(booking.service_price)}</p>
        </div>
        <div>
          <p className="text-slate-400">Travel</p>
          <p className="font-semibold text-slate-900">{formatGBP(booking.travel_fee)}</p>
        </div>
        <div>
          <p className="text-slate-400">Total</p>
          <p className="font-semibold text-slate-900">{formatGBP(booking.total_amount)}</p>
        </div>
        <div>
          <p className="text-rose-500">Commission</p>
          <p className="font-semibold text-rose-700">{formatGBP(booking.platform_commission)}</p>
        </div>
        <div className="col-span-2">
          <p className="text-emerald-600">Your payout</p>
          <p className="font-semibold text-emerald-700">{formatGBP(booking.partner_payout)}</p>
        </div>
      </div>

      <div className="flex gap-2">
        {showAccept && (
          <>
            <Button
              size="sm"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={busy}
              onClick={() => onAction(booking.id, 'accept')}
              data-testid={`btn-accept-${booking.id}`}
            >
              {busy === 'accept' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              <span className="ml-1">Accept</span>
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 border-rose-200 text-rose-700 hover:bg-rose-50"
              disabled={busy}
              onClick={() => onAction(booking.id, 'reject')}
              data-testid={`btn-reject-${booking.id}`}
            >
              {busy === 'reject' ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
              <span className="ml-1">Reject</span>
            </Button>
          </>
        )}
        {showInProgress && (
          <Button
            size="sm"
            className="flex-1 bg-sky-600 hover:bg-sky-700 text-white"
            disabled={busy}
            onClick={() => onAction(booking.id, 'in-progress')}
            data-testid={`btn-inprogress-${booking.id}`}
          >
            {busy === 'in-progress' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
            <span className="ml-1">Start job</span>
          </Button>
        )}
        {showComplete && (
          <Button
            size="sm"
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={busy}
            onClick={() => onAction(booking.id, 'complete')}
            data-testid={`btn-complete-${booking.id}`}
          >
            {busy === 'complete' ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
            <span className="ml-1">Mark complete</span>
          </Button>
        )}
      </div>
    </Card>
  );
}

export default function PartnerBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState({}); // bookingId -> action

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/partner/bookings');
      setBookings(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAction = useCallback(
    async (bookingId, action) => {
      setBusy((s) => ({ ...s, [bookingId]: action }));
      try {
        const { data } = await api.post(`/partner/bookings/${bookingId}/${action}`);
        setBookings((prev) => prev.map((b) => (b.id === bookingId ? data : b)));
        toast.success(`Booking ${action === 'in-progress' ? 'started' : action + 'ed'}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Failed to ${action} booking`);
      } finally {
        setBusy((s) => ({ ...s, [bookingId]: null }));
      }
    },
    [],
  );

  const filtered = useMemo(() => {
    if (filter === 'all') return bookings;
    if (filter === 'requested') return bookings.filter((b) => b.status === 'requested');
    if (filter === 'active') return bookings.filter((b) => ['confirmed', 'accepted', 'in_progress'].includes(b.status));
    if (filter === 'completed') return bookings.filter((b) => b.status === 'completed');
    return bookings;
  }, [bookings, filter]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-900">Bookings</h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-32 h-9" data-testid="bookings-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="requested">Requested</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-center text-sm text-slate-500 py-6">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading bookings...
        </p>
      ) : filtered.length === 0 ? (
        <Card className="p-6 text-center border-slate-200">
          <p className="text-sm text-slate-500">No bookings yet</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((b) => (
            <BookingCard key={b.id} booking={b} onAction={handleAction} busy={busy[b.id]} />
          ))}
        </div>
      )}
    </div>
  );
}
